import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { Add, Delete, Upload, Download } from "@mui/icons-material";
import {
  useReadManySshAccountsQuery,
  useCreateSshAccountMutation,
  useDeleteSshAccountMutation,
  useImportSshAccountsMutation,
  downloadSshAccountTemplate,
  downloadSshAccountsExport,
} from "api/endpoints/ssh-account";
import { useReadManySshCategoriesQuery } from "api/endpoints/ssh-category";
import { toast } from "react-toastify";
import { ConfirmationDialogComponent } from "lib/components/ui/confirmation-dialog";

export const SshAccountsListComponent = ({
  embedded = false,
}: {
  embedded?: boolean;
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importErrors, setImportErrors] = useState<{ row: number; message: string }[]>([]);
  const [form, setForm] = useState({
    categoryId: "",
    host: "",
    port: "22",
    username: "",
    password: "",
  });

  const { data, isLoading, error, refetch } = useReadManySshAccountsQuery({
    query: {
      page: String(page + 1),
      limit: String(rowsPerPage),
    },
  });
  const { data: categoriesData } = useReadManySshCategoriesQuery({
    query: { page: "1", limit: "100" },
  });
  const [createAccount, { isLoading: creating }] = useCreateSshAccountMutation();
  const [deleteAccount] = useDeleteSshAccountMutation();
  const [importAccounts, { isLoading: importing }] = useImportSshAccountsMutation();

  const accounts = data?.sshAccounts || [];
  const categories = categoriesData?.categories || [];

  const handleCreate = async () => {
    try {
      await createAccount({
        payload: {
          categoryId: form.categoryId,
          ssh: {
            host: form.host,
            port: Number(form.port),
            username: form.username,
            password: form.password,
          },
        },
      }).unwrap();
      toast.success("حساب SSH ایجاد شد");
      setCreateOpen(false);
      refetch();
    } catch {
      toast.error("خطا در ایجاد حساب SSH");
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    try {
      const result = await importAccounts({ file: importFile }).unwrap();
      setImportErrors(result.errors);
      toast.success(`${result.imported} حساب با موفقیت وارد شد`);
      if (result.errors.length === 0) {
        setImportOpen(false);
        setImportFile(null);
      }
      refetch();
    } catch {
      toast.error("خطا در import فایل Excel");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteAccount({ params: { sshAccountId: deleteId } }).unwrap();
      toast.success("حساب SSH حذف شد");
      setDeleteId(null);
      refetch();
    } catch {
      toast.error("خطا در حذف حساب SSH");
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  const actionButtons = (
    <Box display="flex" gap={1}>
      <Button
        variant="outlined"
        startIcon={<Download />}
        onClick={downloadSshAccountsExport}
      >
        Export Excel
      </Button>
      <Button
        variant="outlined"
        startIcon={<Download />}
        onClick={downloadSshAccountTemplate}
      >
        دانلود قالب Excel
      </Button>
      <Button
        variant="outlined"
        startIcon={<Upload />}
        onClick={() => {
          setImportFile(null);
          setImportErrors([]);
          setImportOpen(true);
        }}
      >
        Import Excel
      </Button>
      <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
        افزودن حساب
      </Button>
    </Box>
  );

  return (
    <Box p={embedded ? 0 : 3}>
      {!embedded && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5">حساب‌های SSH</Typography>
          {actionButtons}
        </Box>
      )}

      {embedded && (
        <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
          {actionButtons}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          خطا در بارگذاری حساب‌ها
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>تعرفه</TableCell>
                <TableCell>مشخصات</TableCell>
                <TableCell>وضعیت</TableCell>
                <TableCell align="center">عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((acc) => (
                <TableRow key={acc._id}>
                  <TableCell>{acc.categoryName || acc.categoryId}</TableCell>
                  <TableCell>
                    {acc.ssh?.host}:{acc.ssh?.port}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={acc.status === "available" ? "موجود" : "اختصاص یافته"}
                      color={acc.status === "available" ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      disabled={acc.status === "assigned"}
                      onClick={() => setDeleteId(acc._id)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={data?.count || 0}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="تعداد در صفحه"
        />
      </Paper>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>افزودن حساب SSH</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel>تعرفه</InputLabel>
              <Select
                value={form.categoryId}
                label="تعرفه"
                onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
              >
                {categories.map((c) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Host"
              value={form.host}
              onChange={(e) => setForm({ ...form, host: e.target.value })}
              fullWidth
            />
            <TextField
              label="Port"
              value={form.port}
              onChange={(e) => setForm({ ...form, port: e.target.value })}
              fullWidth
            />
            <TextField
              label="Username"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              fullWidth
            />
            <TextField
              label="Password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>انصراف</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !form.categoryId || !form.host}
          >
            ذخیره
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Excel</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
            />
            {importErrors.length > 0 && (
              <Box mt={2}>
                <Alert severity="warning">برخی ردیف‌ها نامعتبر بودند:</Alert>
                <List dense>
                  {importErrors.map((err) => (
                    <ListItem key={err.row}>
                      <ListItemText primary={`ردیف ${err.row}: ${err.message}`} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>بستن</Button>
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!importFile || importing}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialogComponent
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="حذف حساب SSH"
        message="آیا از حذف این حساب اطمینان دارید؟"
        confirmText="حذف"
        confirmColor="error"
      />
    </Box>
  );
};
